import React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { useMobileEdgeChromeStore } from "../../stores/mobileEdgeChromeStore";
import { PORTAL_ABOVE_ALL_PANELS_Z } from "../../utils/panelZIndex";

const SIZE_CLASSES = {
  default: {
    trigger: "min-h-8 px-2 py-1 text-sm",
    item: "px-2 py-1.5 text-sm",
    icon: "h-4 w-4",
  },
  compact: {
    trigger: "min-h-6 px-2 py-1 text-xs",
    item: "px-2 py-1 text-xs",
    icon: "h-3.5 w-3.5",
  },
};

export function normalizeOptions(options) {
  if (!Array.isArray(options)) return [];

  return options.reduce((acc, option) => {
    if (Array.isArray(option?.options)) {
      const groupOptions = normalizeOptions(option.options);
      if (groupOptions.length > 0) {
        acc.push({ ...option, options: groupOptions });
      }
      return acc;
    }

    if (String(option?.value ?? "") !== "") {
      acc.push(option);
    }
    return acc;
  }, []);
}

function MenuSelect({
  value,
  onChange,
  options,
  placeholder = "Select...",
  disabled = false,
  size = "default",
  className = "",
  triggerClassName = "",
  contentClassName = "",
  align = "start",
  sideOffset = 4,
  triggerRef,
  triggerProps = {},
  onOpenChange: onOpenChangeProp,
  "aria-label": ariaLabel,
}) {
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.default;
  const normalizedOptions = normalizeOptions(options);
  const triggerElRef = React.useRef(null);
  const setTriggerEl = React.useCallback(
    (node) => {
      triggerElRef.current = node;
      if (typeof triggerRef === "function") {
        triggerRef(node);
      } else if (triggerRef) {
        triggerRef.current = node;
      }
    },
    [triggerRef]
  );

  const handleOpenChange = React.useCallback(
    (open) => {
      if (
        open &&
        typeof document !== "undefined" &&
        document.activeElement === triggerElRef.current
      ) {
        triggerElRef.current?.blur();
      }
      useMobileEdgeChromeStore.getState().actions.notifyMenuOverlayOpenChange(open);
      onOpenChangeProp?.(open);
    },
    [onOpenChangeProp]
  );

  const renderItem = (option) => (
    <SelectPrimitive.Item
      key={option.value}
      value={String(option.value)}
      disabled={option.disabled}
      className={[
        "relative flex w-full cursor-default select-none items-center rounded text-gray-100 outline-none",
        "data-[highlighted]:bg-gray-600/80 data-[disabled]:pointer-events-none data-[disabled]:opacity-45",
        "data-[state=checked]:bg-gray-600/70",
        sizeClasses.item,
      ].join(" ")}
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <SelectPrimitive.ItemText>
          <span className="truncate">{option.label}</span>
        </SelectPrimitive.ItemText>
        {option.description ? (
          <span className="whitespace-normal text-[11px] leading-4 text-gray-400">
            {option.description}
          </span>
        ) : null}
      </span>
      <SelectPrimitive.ItemIndicator className="ml-auto inline-flex items-center pl-2 text-gray-200">
        <Check className={sizeClasses.icon} aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );

  return (
    <SelectPrimitive.Root
      value={value == null ? "" : String(value)}
      onValueChange={onChange}
      disabled={disabled}
      onOpenChange={handleOpenChange}
    >
      <SelectPrimitive.Trigger
        ref={setTriggerEl}
        aria-label={ariaLabel}
        className={[
          "flex w-full min-w-0 items-center justify-between gap-2 rounded border border-gray-600 bg-gray-800 text-gray-100",
          "outline-none transition-colors hover:border-gray-500 hover:bg-gray-700/80",
          "focus-visible:border-gray-400 focus-visible:ring-2 focus-visible:ring-gray-500/50",
          "disabled:cursor-not-allowed disabled:opacity-50",
          sizeClasses.trigger,
          className,
          triggerClassName,
        ]
          .filter(Boolean)
          .join(" ")}
        {...triggerProps}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            className={`${sizeClasses.icon} shrink-0 text-gray-400`}
            aria-hidden="true"
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          align={align}
          sideOffset={sideOffset}
          className={[
            "overflow-hidden rounded-md border border-gray-700 bg-gray-800/95 p-1 text-gray-100 shadow-xl backdrop-blur",
            contentClassName,
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ zIndex: PORTAL_ABOVE_ALL_PANELS_Z }}
        >
          <SelectPrimitive.Viewport className="max-h-[70vh] min-w-[var(--radix-select-trigger-width)] overflow-auto">
            {normalizedOptions.map((entry) =>
              Array.isArray(entry.options) ? (
                <SelectPrimitive.Group key={entry.label}>
                  <SelectPrimitive.Label className="px-2 py-1 text-[11px] font-medium text-gray-400">
                    {entry.label}
                  </SelectPrimitive.Label>
                  {entry.options.map(renderItem)}
                </SelectPrimitive.Group>
              ) : (
                renderItem(entry)
              )
            )}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export default MenuSelect;
